import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Lesson } from "../../types";
import { sendChat } from "../../utils/api";
import { Banner, Icon } from "../common/ui";

/**
 * The two things a student does after watching: send the work in, or ask about
 * it. They sit together as a matched pair, because from the student's side they
 * are one decision — "I need Anya" — split by which kind of help is needed.
 *
 * Both come from the «Проверка работ и чат» block. A lessons-only subscription
 * sees them replaced by a single card explaining what it buys, rather than two
 * dead buttons; the server refuses either call regardless of what is rendered.
 */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function LessonActions({
  lesson, title, token, onAsk, onOpenPlans,
}: {
  lesson: Lesson;
  title: string;
  token: string;
  onAsk: (subject: string, lessonId: string) => void;
  onOpenPlans?: () => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One task per lesson is the norm, and then this button is the whole story.
  // A lesson with SEVERAL tasks cannot be served by one button — the student
  // would have no way to say which work this is — so there the per-task panels
  // above keep their own send buttons and this pair drops to just the question.
  const tasks = lesson.exercises ?? [];
  const exercise = tasks.length === 1 ? tasks[0] : undefined;
  const canSubmit = lesson.can_submit !== false && !!exercise;
  const canAsk = lesson.can_ask !== false;

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !exercise) return;
    setSending(true);
    setError(null);
    try {
      await sendChat(token, { photo_base64: await fileToDataUrl(file), exercise_id: exercise.id });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setSending(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!canAsk) {
    // Not owning the course = a demo lesson; owning it = a lessons-only plan.
    const demo = lesson.owns_course === false;
    return (
      <div
        className="glass glass-lg"
        style={{ padding: 16, marginBottom: 20, border: "1px dashed var(--border)" }}
      >
        <p className="t-sm" style={{ margin: 0, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="lock" size={16} /> {demo ? t("exercise.subscriptionToSubmit") : t("lessonActions.noFeedbackTitle")}
        </p>
        <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.5 }}>
          {demo ? t("exercise.subscriptionToSubmitHint") : t("lessonActions.noFeedbackHint")}
        </p>
        {onOpenPlans && (
          <button className="btn btn-violet btn-block" onClick={onOpenPlans} style={{ marginTop: 12 }}>
            <Icon name="sparkles" size={17} /> {t("lessonActions.seePlans")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} />

      <div className="lesson-actions">
        {canSubmit && (
          <button
            className="btn btn-violet"
            onClick={() => fileRef.current?.click()}
            disabled={sending}
            style={{ flex: "1 1 190px", paddingBlock: 13 }}
          >
            {sending
              ? <span className="spinner" />
              : <><Icon name="image" size={18} /> {t("lessonActions.sendWork")}</>}
          </button>
        )}
        <button
          className={canSubmit ? "btn btn-glass" : "btn btn-violet"}
          onClick={() => onAsk(title, lesson.id)}
          style={{ flex: "1 1 190px", paddingBlock: 13 }}
        >
          <Icon name="message" size={18} /> {t("lessonActions.askQuestion")}
        </button>
      </div>

      {sent && <div style={{ marginTop: 10 }}><Banner kind="success">{t("exercise.workSent")}</Banner></div>}
      {error && <div style={{ marginTop: 10 }}><Banner kind="error">{error}</Banner></div>}
    </div>
  );
}
